import { type DishInMeal, type MealsPlanDetails } from "@fitness-app/data-models/entities/MealsPlan";

/**
 * Wybór potraw do masowej generacji zdjęć (TRE-1830).
 *
 * Mieszka we wspólnym pakiecie, bo liczą po nim dwie strony i MUSZĄ dostać ten sam wynik: panel
 * pokazuje trenerce, ile zdjęć powstanie i ile to będzie kosztować, a task
 * `generate-meals-plan-dish-images-task` faktycznie je generuje. Rozjazd tych dwóch liczb to
 * dokładnie ta sytuacja, w której trenerka klika „5 zdjęć", a na rachunku ląduje 40.
 */

type PlanWeeks = MealsPlanDetails["weeks"];

/**
 * Potrawę rozpoznajemy NEGATYWNIE — wszystko, co nie jest składnikiem dorzuconym luzem.
 *
 * `type` bywa nullem: w `meals_plan_details` na stagingu 21 163 potraw ma `type: null`, a tylko
 * 5 518 ma literalne `"dish"` (w planach klientek proporcja jest ta sama). Sprawdzanie
 * `type === "dish"` przepuszczało niecałe 21% potraw. Reszta repo — `reduceMealsNutrients`,
 * `getShoppingList`, `generateMealsPlanDetails` — od zawsze porównuje z `"ingredient"` i to jest
 * konwencja, której trzeba się tu trzymać.
 */
export const isDishInMeal = (item: unknown): item is DishInMeal => {
  // Świadomie luźniejszy typ niż `DishInMeal`: encja deklaruje `type: "dish"`, ale w bazie ta
  // kolumna jest zwykłym stringiem i najczęściej jest pusta. Porównanie na `DishInMeal` nie
  // przechodzi type-checka właśnie dlatego, że typ obiecuje coś, czego dane nie dotrzymują.
  const candidate = item as { type?: string | null; name?: unknown } | null | undefined;

  return (
    Boolean(candidate) &&
    candidate?.type !== "ingredient" &&
    typeof candidate?.name === "string" &&
    Boolean(candidate.name.trim())
  );
};

export const hasDishCoverPhoto = (dish: DishInMeal): boolean => Boolean(dish.media?.some((media) => media?.url));

/**
 * Klucz deduplikacji. Ta sama potrawa wraca w wielu dniach i posiłkach jako osobne kopie —
 * `parentId` wskazuje wspólny wiersz w bibliotece, a potrawy dodane ad-hoc mają tylko własne id.
 */
export const getDishImageKey = (dish: DishInMeal): string => dish.parentId || dish.id;

/**
 * Unikalne potrawy w planie, którym trzeba wygenerować zdjęcie. Wartością mapy jest PIERWSZE
 * wystąpienie — to z niego bierzemy nazwę i składniki do promptu.
 */
export const collectDishesToIllustrate = (
  weeks: PlanWeeks | null | undefined,
  { overwrite = false }: { overwrite?: boolean } = {},
): Map<string, DishInMeal> => {
  const dishes = new Map<string, DishInMeal>();

  for (const week of weeks || []) {
    for (const day of week.days || []) {
      for (const meal of day.meals || []) {
        for (const item of meal.dishes || []) {
          if (!isDishInMeal(item) || (!overwrite && hasDishCoverPhoto(item))) {
            continue;
          }
          const key = getDishImageKey(item);
          if (!dishes.has(key)) {
            dishes.set(key, item);
          }
        }
      }
    }
  }

  return dishes;
};

/**
 * Wpisuje wygenerowane zdjęcia (mapa `klucz potrawy` → URL) do wystąpień w planie. Mutuje `weeks`
 * w miejscu i zwraca liczbę podmienionych pozycji.
 *
 * Druga połowa reguły z `collectDishesToIllustrate` i dlatego siedzi obok: potrawa kwalifikuje się
 * do generacji, gdy choć JEDNA jej kopia jest bez zdjęcia, ale każda kopia ma własne `media`.
 * Bez `overwrite` wolno więc dopisać zdjęcie tylko tam, gdzie go nie ma — inaczej skasowalibyśmy
 * zdjęcie, które trenerka ustawiła ręcznie w konkretnym dniu.
 */
export const applyDishImagesToWeeks = (
  weeks: PlanWeeks | null | undefined,
  images: Map<string, string>,
  { overwrite = false }: { overwrite?: boolean } = {},
): number => {
  let updatedOccurrences = 0;

  for (const week of weeks || []) {
    for (const day of week.days || []) {
      for (const meal of day.meals || []) {
        for (const item of meal.dishes || []) {
          if (!isDishInMeal(item)) {
            continue;
          }

          const url = images.get(getDishImageKey(item));

          if (!url || (!overwrite && hasDishCoverPhoto(item))) {
            continue;
          }

          item.media = [{ url, type: "coverPhoto" }];
          updatedOccurrences += 1;
        }
      }
    }
  }

  return updatedOccurrences;
};
